<template>
    <div id="test-app-dt">
        <div class="container">
            <DataTable
                :columns="voucherColumns"
                :items="items"
                :current-page="currentPage"
                :total-pages="totalPages"
                :enable-multi-select="false"
                :enable-drag-drop="false"
                @page-changed="handlePageChange"
                @sort="onSort"
            >
                <template #toolbar>
                    <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                        <div class="ppv2-flex-1"></div>

                        <!-- Search Input -->
                        <div class="ppv2-relative">
                            <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                        </div>
                    </div>
                </template>

                <template #column-invoice="{ item }">
                    <a
                        v-if="item.invoice.has_invoice"
                        :href="getInvoiceUrl(item.invoice.id)"
                        class="text-blue-600 hover:underline"
                        target="_blank"
                    >
                        Voir la facture
                    </a>
                    <span v-else class="text-gray-500">Facture non disponible</span>
                </template>

                <template #column-purchase="{ item }">
                    <ul class="ps-0" style="list-style-type: none">
                        <li>{{ item.purchase.label }}</li>
                    </ul>
                </template>

                <template #column-delivery="{ item }">
                    <span v-html="item.delivery"></span>
                </template>

                <template #column-to="{ item }">
                    <span v-html="item.to"></span>
                </template>

                <template #column-balance="{ item }">
                    <ul class="ps-0" style="list-style-type: none">
                        <li>{{ item.balance.formatted }}</li>
                    </ul>
                </template>

                <template #column-prestations_prices="{ item }">
                    <ul class="ps-0" style="list-style-type: none">
                        <li v-for="(price, index) in item.prestations_prices" :key="index">
                            {{ price.label }} : {{ price.value }}
                        </li>
                    </ul>
                </template>

                <template #column-status="{ item }">
                    <span :class="getStatus(item).class">
                        {{ $t(getStatus(item).key) }}
                    </span>
                </template>

                <template #column-actions="{ item }">
                    <div
                        class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                        :style="{ fontSize: normalizeFontSize(18) }"
                    >
                        <!-- Download -->
                        <a
                            :href="`/back-office/bons_cadeau/${item.voucher_id}`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_vouchers_purchased_v2_edit.actions.btn_donwload.title')"
                        >
                            <Download :size="18" />
                        </a>
                        <!-- Edit -->
                        <a
                            :href="`/back-office/bons_cadeau/${item.voucher_id}/edit`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_vouchers_purchased_v2_edit.actions.btn_modify.title')"
                        >
                            <SquarePen :size="18" />
                        </a>
                        <!-- History -->
                        <a
                            href="#"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_vouchers_purchased_v2_edit.actions.btn_history.title')"
                        >
                            <History :size="18" />
                        </a>
                        <!-- Send Email -->
                        <a
                            :href="`/back-office/bons_cadeau/${item.voucher_id}/send_voucher`"
                            class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :title="$t('datatable_customer_vouchers_purchased_v2_edit.actions.btn_send_email.title')"
                        >
                            <Mail :size="18" />
                        </a>
                        <!-- Enable/Disable -->
                        <button
                            @click="handleToggleStatus(item)"
                            class="ppv2-p-1 hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                            :class="
                                item.is_blocked === 0
                                    ? 'ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C]'
                                    : 'ppv2-text-green-600 hover:ppv2-text-green-600 hover:ppv2-bg-green-600'
                            "
                            :title="
                                item.is_blocked === 0
                                    ? $t('datatable_customer_vouchers_purchased_v2_edit.actions.btn_disabled.title')
                                    : $t('datatable_customer_vouchers_purchased_v2_edit.actions.btn_enabled.title')
                            "
                        >
                            <Ban v-if="item.is_blocked === 0" :size="18" />
                            <CheckCircle v-else :size="18" />
                        </button>
                    </div>
                </template>
            </DataTable>
        </div>
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import { Download, SquarePen, History, Mail, Ban, CheckCircle } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'App',
    props: ['user'],
    components: {
        DataTable,
        Download,
        SquarePen,
        History,
        Mail,
        Ban,
        CheckCircle,
        Search,
    },
    data() {
        return {
            // Data Table State
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,

            // API Configuration
            apiHeaders: {
                Authorization: 'Bearer your-token-here',
                'X-Requested-With': 'XMLHttpRequest',
            },

            // Users Table Configuration
            voucherPurhaseApiUrl: '/back-office/v2/customer/' + this.user.id + '/ajaxVoucherPurchase',
            voucherColumns: [
                {
                    key: 'code',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.code'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'purchase',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.purchase'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'invoice',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.invoice'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'delivery',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.delivery'),
                    sortable: true,
                    type: 'text',
                    type: 'text',
                },
                {
                    key: 'to',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.to'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'send_date',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.send_date'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'date_end',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.date_end'),
                    sortable: true,
                    type: 'date',
                },
                {
                    key: 'prestations_prices',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.prestations_prices'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'balance',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.balance'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'status',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status'),
                    sortable: true,
                    type: 'badge',
                },
                {
                    key: 'actions',
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.action'),
                    sortable: false,
                    type: 'actions',
                },
            ],
        }
    },
    computed: {
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.voucherPurhaseApiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
                this.onDataLoaded({ data: this.items, total: this.totalEntries })
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        onDataLoaded(response) {
            console.log('Data loaded:', response)
        },
        onError(error) {
            console.error('Datatable error:', error)
            alert('Error loading data: ' + error.message)
        },
        getStatus(item) {
            const now = new Date()
            let status = {
                label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_used'),
                class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_used'),
            }

            const dateEnd = new Date(item.date_end)
            const sendDate = new Date(item.send_date)

            if (dateEnd < now) {
                status = {
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.expired'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-red-600 ppv2-text-red-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                    key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.expired'),
                }
            } else if (sendDate > now) {
                status = {
                    label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_sent'),
                    class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-red-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                    key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_sent'),
                }
            } else if (item.available_amount < item.total_amount) {
                if (item.available_amount === 0) {
                    status = {
                        label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.totally_used'),
                        class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-red-600 ppv2-text-red-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.totally_used'),
                    }
                } else {
                    status = {
                        label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.partially_used'),
                        class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-blue-600 ppv2-text-blue-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                        key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.partially_used'),
                    }
                }
            } else if (item.available_amount === item.total_amount) {
                for (const voucherItem of item.items) {
                    if (voucherItem.item_type === 'App\\Models\\Prestation\\Prestation') {
                        status =
                            voucherItem.available_quantity === 0
                                ? {
                                      label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.partially_used'),
                                      class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-blue-600 ppv2-text-blue-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                                      key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.partially_used'),
                                  }
                                : {
                                      label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_used'),
                                      class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                                      key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_used'),
                                  }
                    } else if (voucherItem.item_type === 'App\\Models\\Voucher\\Voucher') {
                        status =
                            voucherItem.available_amount !== voucherItem.amount
                                ? {
                                      label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.partially_used'),
                                      class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-blue-600 ppv2-text-blue-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                                      key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.partially_used'),
                                  }
                                : {
                                      label: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_used'),
                                      class: 'ppv2-inline-block ppv2-rounded-full ppv2-bg-green-600 ppv2-text-green-100 ppv2-text-xs ppv2-px-3 ppv2-py-1',
                                      key: this.$t('datatable_customer_vouchers_purchased_v2_edit.status_item.not_used'),
                                  }
                    }
                }
            }

            return status
        },
        getInvoiceUrl(id) {
            return `${window.location.origin}/back-office/invoice/${id}`
        },
        handleToggleStatus(item) {
            const form = document.createElement('form')
            form.method = 'POST'
            form.action =
                item.is_blocked === 0
                    ? `${window.location.origin}/back-office/bons_cadeau/${item.voucher_id}/disable`
                    : `${window.location.origin}/back-office/bons_cadeau/${item.voucher_id}/enable`

            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
            if (csrfToken) {
                const csrfInput = document.createElement('input')
                csrfInput.type = 'hidden'
                csrfInput.name = '_token'
                csrfInput.value = csrfToken
                form.appendChild(csrfInput)
            }

            const voucherInput = document.createElement('input')
            voucherInput.type = 'hidden'
            voucherInput.name = 'voucher'
            voucherInput.value = item.voucher_id
            form.appendChild(voucherInput)

            document.body.appendChild(form)
            form.submit()
            document.body.removeChild(form)
        },
    },
}
</script>

<style scoped>
.container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
}
</style>
